/**
 * Renders the design tokens as CSS custom properties.
 *
 * The CSS file this produces is COMMITTED, not built at runtime — a generated
 * artefact you can read in review beats a build step nobody can see. What keeps
 * the two in step is a test (`web/tests/unit/tokensCss.test.ts`) asserting the
 * committed file is byte-identical to this function's output. Regenerate with:
 *
 *   npm run tokens
 *
 * That arrangement is deliberate: CSS variables and TypeScript constants
 * maintained as parallel hand-written lists is precisely how this codebase
 * arrived at three competing colour ramps.
 */
import { MIN_TAP_TARGET, radius, spacing } from './geometry';
import { FADE_MS, STAGGER_MS, springEasing, springSettleSeconds } from './motion';
import { designTokens } from './surfaces';
import { designTokensDark } from './theme-dark';
import { FONT_FAMILY, TABULAR_FEATURES, TYPE_ROLES, fluidType, frontDoorScale, typeScale } from './typography';

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** Grouped so the emitted file reads like the design system, not like a dump. */
const GROUPS: { title: string; note: string; tokens: (keyof typeof designTokens)[] }[] = [
  {
    title: 'Surfaces',
    note: 'One cool family, four even steps in OKLCH lightness. What you press darkens; what you write on is the brightest.',
    tokens: ['hover', 'page', 'paper', 'raised'],
  },
  {
    title: 'Lines',
    note: 'Ink at low alpha, so borders stay in the hue family of the text they enclose.',
    tokens: ['hairline', 'hairlineStrong'],
  },
  {
    title: 'Text',
    note: 'Three tiers. Anything below ink-faint is not text, it is decoration.',
    tokens: ['ink', 'inkDim', 'inkFaint'],
  },
  {
    title: 'Accent',
    note: 'Marks the current position and nothing else. accent = graphics, accent-ink = text.',
    tokens: ['accent', 'accentInk', 'accentBright', 'accentSoft', 'accentLine'],
  },
  {
    title: 'Action',
    note: 'The CTA is near-black ink, NOT the accent — position and action stay distinct.',
    tokens: ['button', 'buttonLabel', 'buttonHover'],
  },
  {
    title: 'Status',
    note: 'Reports an outcome. Never encodes a measurement.',
    tokens: ['danger', 'warn', 'ok'],
  },
  {
    title: 'Seat',
    note: 'The listener. A documented single-hue exception — see surfaces.ts.',
    tokens: ['seat'],
  },
  {
    title: 'Depth',
    note: 'Shadows and scrims. Ink on paper; stays dark when ink turns light.',
    tokens: ['shadow'],
  },
];

/**
 * The COLOUR tokens only, for one theme, under any selector.
 *
 * Geometry, motion and type do not change between themes, so a theme block
 * redeclares exactly the `--rt-*` colours and nothing else. Every `--app-*` /
 * `--ed-*` alias and every Tailwind utility reads through these, which is what
 * makes a theme one block of custom properties rather than a pass over 2,000
 * class names.
 *
 * `color-scheme` rides along so native controls, scrollbars and autofill paint
 * in the matching register.
 */
export function renderThemeCss(
  tokens: Record<keyof typeof designTokens, string>,
  selector: string,
  scheme: 'light' | 'dark',
): string {
  const lines = [`${selector} {`, `  color-scheme: ${scheme};`];
  for (const group of GROUPS) {
    for (const token of group.tokens) lines.push(`  --rt-${kebab(token)}: ${tokens[token]};`);
  }
  lines.push('}', '');
  return lines.join('\n');
}

export function renderTokensCss(): string {
  const lines: string[] = [
    '/*',
    ' * GENERATED by packages/design/src/css-tokens.ts — do not edit by hand.',
    ' * Run `npm run tokens` from the repo root after changing the palette.',
    ' *',
    ' * Every value is derived from packages/design/src/palette.ts, which is shared',
    ' * with the mobile app. Editing a colour here will be overwritten and will make',
    ' * tokensCss.test.ts fail; change the derivation instead.',
    ' */',
    '',
    ':root {',
  ];

  for (const group of GROUPS) {
    lines.push(`  /* ${group.title} — ${group.note} */`);
    // `--rt-` + longest name + `:`, so the values line up within a group.
    const width = 5 + Math.max(...group.tokens.map((t) => kebab(t).length)) + 1;
    for (const token of group.tokens) {
      const decl = `--rt-${kebab(token)}:`;
      lines.push(`  ${decl.padEnd(width)} ${designTokens[token]};`);
    }
    lines.push('');
  }

  lines.push('  /* Shape — 4px grid. Buttons are pills; that is a signature, not a preference. */');
  for (const [name, value] of Object.entries(radius)) {
    lines.push(`  --rt-radius-${name}: ${value}px;`);
  }
  lines.push('');

  lines.push('  /* Rhythm — the same scale the app spaces on. */');
  for (const [name, value] of Object.entries(spacing)) {
    lines.push(`  --rt-space-${name}: ${value}px;`);
  }
  lines.push(`  --rt-tap-min: ${MIN_TAP_TARGET}px;`);
  lines.push('');

  lines.push('  /* Motion — the mobile app’s spring, solved and sampled. Never a');
  lines.push('     cubic-bezier: see packages/design/src/motion.ts. */');
  lines.push(`  --rt-ease-spring: ${springEasing()};`);
  lines.push(`  --rt-spring-ms: ${Math.round(springSettleSeconds() * 1000)}ms;`);
  lines.push(`  --rt-stagger-ms: ${STAGGER_MS}ms;`);
  lines.push(`  --rt-fade-ms: ${FADE_MS}ms;`);
  lines.push('');

  lines.push('  /* Type — one family, four weights. Numerals are tabular everywhere.');
  lines.push('     The platform supplies the actual family through --font-google-sans:');
  lines.push('     next/font emits a hashed name, so the literal below is only a fallback');
  lines.push('     for contexts that load the face themselves. */');
  lines.push(`  --rt-font: var(--font-google-sans, '${FONT_FAMILY}'), system-ui, sans-serif;`);
  lines.push(`  --rt-numeric: ${TABULAR_FEATURES};`);
  lines.push('');

  lines.push('  /* Type scale — one ladder, two anchors (390px and 1280px). Display type');
  lines.push('     interpolates between them; text type holds one size. globals.css maps');
  lines.push('     these into @theme so each role is a `text-<role>` utility carrying its');
  lines.push('     own leading and tracking. See packages/design/src/typography.ts. */');
  for (const role of TYPE_ROLES) {
    const step = typeScale[role];
    lines.push(`  --rt-text-${role}: ${fluidType(step)};`);
    lines.push(`  --rt-text-${role}-leading: ${step.leading};`);
    lines.push(`  --rt-text-${role}-tracking: ${step.tracking}em;`);
  }
  lines.push('}');
  lines.push('');

  // The front door — the same roles, set louder, only under `.rt-front-door`
  // (typography.ts `frontDoorScale`). Scoped variables, so every `text-<role>`
  // utility beneath it moves without a class changing.
  lines.push('/* Front door — the same type ladder, set louder. See frontDoorScale. */');
  lines.push('.rt-front-door {');
  for (const role of TYPE_ROLES) {
    const step = frontDoorScale[role];
    lines.push(`  --rt-text-${role}: ${fluidType(step)};`);
    lines.push(`  --rt-text-${role}-leading: ${step.leading};`);
    lines.push(`  --rt-text-${role}-tracking: ${step.tracking}em;`);
  }
  lines.push('}');
  lines.push('');

  // Themes — colours only, see renderThemeCss. `:root[data-theme]` outranks
  // the bare `:root` block above by one attribute, so the theme wins whatever
  // order the stylesheets land in; the bare `[data-theme]` half lets any
  // subtree carry its own theme. The attribute is written before first paint
  // by the boot script in web/src/lib/theme/boot.ts. Light is restated so a
  // light island inside a dark page (or vice versa) resolves correctly.
  lines.push('/* Dark — the same system reflected: ink ↔ page. See theme-dark.ts. */');
  lines.push(renderThemeCss(designTokensDark, THEME_SELECTOR.dark, 'dark'));
  lines.push('/* Light, restated for light subtrees inside a dark page. */');
  lines.push(renderThemeCss(designTokens, THEME_SELECTOR.light, 'light'));

  return lines.join('\n');
}

/** The selectors a theme block is emitted under. Exported for the web tests
 *  that need to tell theme scopes apart from the base `:root`. */
export const THEME_SELECTOR = {
  dark: ':root[data-theme="dark"], [data-theme="dark"]',
  light: ':root[data-theme="light"], [data-theme="light"]',
} as const;
